import React, { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import SchoolRounded from '@mui/icons-material/SchoolRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { getMeCached } from '../authCache';
import { fetchClasses } from '../stargarden/api/starGardenApi';
import { fetchMiniTaskClassOutcomes } from '../api/miniTasks';
import { getStudentAvatar } from '../../shared/studentAvatar';
import './MiniTaskOutcomesPage.css';

const STATUS = {
  good: 'İyi',
  practice: 'Pekiştirilmeli',
  support: 'Destek gerekli',
  insufficient: 'Yeterli veri yok',
};
const STATUS_KEYS = Object.keys(STATUS);
const DEFAULT_LESSON_IMAGE = '/mini-tasks/outcomes/default-outcomes-hero.png';

function classLabel(item) {
  return String(item?.name || item?.code || item?.id || '').trim();
}

function StudentAvatar({ student, size = 42 }) {
  const theme = getStudentAvatar(student.id);
  return <Avatar
    className="mo-student-avatar"
    sx={{ width: size, height: size, bgcolor: theme.bgColor, borderColor: theme.borderColor, fontSize: size * 0.52 }}
    aria-label={`${student.name} hayvan avatarı`}
  >{theme.avatar}</Avatar>;
}

function StatusPill({ status }) {
  return <span className={`mo-status mo-status--${status}`}>{STATUS[status] || STATUS.insufficient}</span>;
}

function Distribution({ counts = {} }) {
  const total = STATUS_KEYS.reduce((sum, key) => sum + Number(counts[key] || 0), 0);
  return (
    <div className="mo-distribution" aria-label="Öğrenci durum dağılımı">
      <div className="mo-distribution__bar" aria-hidden="true">
        {STATUS_KEYS.map((key) => <span key={key} className={`mo-distribution__part mo-distribution__part--${key}`} style={{ width: `${total ? 100 * Number(counts[key] || 0) / total : 0}%` }} />)}
      </div>
      <div className="mo-distribution__labels">
        {STATUS_KEYS.filter((key) => counts[key]).map((key) => <span key={key}>{counts[key]} {STATUS[key].toLocaleLowerCase('tr-TR')}</span>)}
      </div>
    </div>
  );
}

function OutcomeLabel({ outcome }) {
  const mathOutcome = Object.prototype.hasOwnProperty.call(outcome, 'title_verified');
  const title = mathOutcome
    ? (outcome.title_verified && outcome.official_title ? outcome.official_title : 'Resmî ad doğrulanmadı')
    : outcome.text;
  return <span><strong>{outcome.code}</strong> {title}
    {(outcome.level_focuses || []).map((item) => <small className="mo-level-focus" key={item.source_level}>İçerik L{item.source_level}: {item.focus}</small>)}
  </span>;
}

function OutcomeEvidence({ evidence }) {
  return <><StatusPill status={evidence.status} /><span className="mo-evidence-count">{evidence.correct_count}/{evidence.attempt_count} doğru{evidence.accuracy_percent != null ? ` · %${evidence.accuracy_percent}` : ''}</span></>;
}

export default function MiniTaskOutcomesPage() {
  const [schoolId, setSchoolId] = useState(null);
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const [tab, setTab] = useState('outcomes');
  const [areaId, setAreaId] = useState(null);
  const [outcomeId, setOutcomeId] = useState(null);
  const [studentId, setStudentId] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const me = await getMeCached();
        const id = me?.data?.admin_user?.school_id || me?.admin_user?.school_id;
        if (!id) throw new Error('Okul bilgisi bulunamadı.');
        const rows = await fetchClasses(id);
        if (!active) return;
        const ordered = (Array.isArray(rows) ? rows : [])
          .filter((item) => item?.id != null && classLabel(item))
          .sort((a, b) => classLabel(a).localeCompare(classLabel(b), 'tr-TR'));
        setSchoolId(id);
        setClasses(ordered);
        setClassId(ordered[0]?.id != null ? String(ordered[0].id) : '');
      } catch (requestError) {
        if (active) setError(requestError?.message || 'Sınıflar yüklenemedi.');
      } finally {
        if (active) setLoadingClasses(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!schoolId || !classId) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    fetchMiniTaskClassOutcomes(schoolId, classId, lessonId || undefined, { days })
      .then((payload) => { if (active) setData(payload); })
      .catch((requestError) => { if (active) setError(requestError?.message || 'Kazanım durumu yüklenemedi.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [schoolId, classId, lessonId, days, reloadKey]);

  const selectedOutcome = useMemo(() => data?.areas?.flatMap((area) => area.outcomes || []).find((item) => item.id === outcomeId), [data, outcomeId]);
  const selectedStudent = data?.students?.find((item) => item.id === studentId);
  const selectedClass = classes.find((item) => String(item.id) === classId);
  const summary = data?.summary;
  const accuracy = summary?.attempt_count ? Math.round(100 * summary.correct_count / summary.attempt_count) : null;

  const changeClass = (event) => {
    setClassId(event.target.value);
    setLessonId('');
    setAreaId(null);
    setOutcomeId(null);
    setStudentId(null);
  };
  const changeLesson = (event) => {
    setLessonId(event.target.value);
    setAreaId(null);
    setOutcomeId(null);
    setStudentId(null);
  };

  return (
    <Box className="mo-page">
      <main className="mo-main">
        <header className="mo-heading">
          <div className="mo-heading__copy"><span className="mo-heading__icon"><SchoolRounded /></span><div><Typography component="h1">Mini Görev Kazanımları</Typography><Typography>Öğrencilerinizin kazanım durumunu Mini Görev yanıtlarıyla izleyin.</Typography></div></div>
          <div className="mo-controls">
            <label>Sınıf<select aria-label="Sınıf" value={classId} onChange={changeClass} disabled={loadingClasses || !classes.length}>{classes.map((item) => <option key={item.id} value={String(item.id)}>{classLabel(item)}</option>)}</select></label>
            <label>Ders<select aria-label="Ders" value={lessonId || String(data?.selected_lesson_id || '')} onChange={changeLesson} disabled={!data?.lessons?.length}>{(data?.lessons || []).map((item) => <option key={item.id} value={String(item.id)}>{item.name}</option>)}</select></label>
            <label className="mo-period-label">Dönem<select className="mo-period-select" aria-label="Dönem" value={days} onChange={(event) => setDays(Number(event.target.value))} disabled={!classId}>{[7, 30, 90].map((value) => <option key={value} value={value}>Son {value} gün</option>)}</select></label>
            <Button variant="outlined" startIcon={<RefreshRounded />} disabled={!classId || loading} onClick={() => setReloadKey((value) => value + 1)}>Yenile</Button>
          </div>
        </header>

        {error && <Alert severity="error" className="mo-alert">{error}</Alert>}
        {(loadingClasses || loading) && <div className="mo-loading" aria-label="Kazanım verileri yükleniyor"><Skeleton variant="rounded" height={170} /><Skeleton variant="rounded" height={90} /><Skeleton variant="rounded" height={90} /></div>}
        {!loadingClasses && !classes.length && !error && <div className="mo-empty">Görüntülenebilecek sınıf bulunamadı.</div>}
        {!loading && data && !data.lessons.length && <div className="mo-empty"><strong>{classLabel(selectedClass)} için kazanım verisi yok.</strong><span>Soru–kazanım eşlemesi bulunan Din Kültürü, 2–3. sınıf Matematik Yolculuğu ve 3. sınıf İngilizce Yolculuğu içerikleri gösterilebilir.</span></div>}
        {!loading && data && data.lessons.length > 0 && <>
          <section className="mo-hero" aria-label="Kazanım özeti">
            <img className="mo-hero__image" src={DEFAULT_LESSON_IMAGE} alt="Kitapların üzerinde kazanım listesi ve kalem" width="160" height="160" />
            <div className="mo-hero__main"><span className="mo-eyebrow">Son {data.days} gün · Mini Görev performansı</span><h2>{data.lessons.find((item) => item.id === data.selected_lesson_id)?.name}</h2><p>{classLabel(selectedClass)} sınıfında {summary.participating_student_count} / {summary.student_count} öğrenci çalıştı</p></div>
            <div className="mo-metrics"><div><strong>{summary.attempt_count.toLocaleString('tr-TR')}</strong><span>yanıtlanan soru</span></div><div><strong>{accuracy == null ? '—' : `%${accuracy}`}</strong><span>doğruluk</span></div><div><strong>{summary.outcome_count}</strong><span>kazanım</span></div></div>
          </section>
          <div className={`mo-notice ${summary.support_outcome_count ? 'mo-notice--attention' : ''}`}><strong>{summary.support_outcome_count ? `${summary.support_outcome_count} kazanımda destek ihtiyacı` : 'Destek gerekli görünen kazanım yok'}</strong><span>{summary.support_outcome_count ? `${summary.support_student_count} öğrencinin yeterli sayıda yanıtında doğruluk %60'ın altında.` : 'Yeterli veri olmayan kazanımlar ayrı gösterilir.'}</span></div>
          <div className="mo-tabs" role="tablist" aria-label="Kazanım görünümü"><button type="button" role="tab" aria-selected={tab === 'outcomes'} className={tab === 'outcomes' ? 'is-active' : ''} onClick={() => setTab('outcomes')}>Kazanımlar</button><button type="button" role="tab" aria-selected={tab === 'students'} className={tab === 'students' ? 'is-active' : ''} onClick={() => setTab('students')}>Öğrenciler</button></div>
          {tab === 'outcomes' && <section className="mo-content" aria-label="Öğrenme alanları"><div className="mo-section-heading"><div><h2>{data.lessons.find((item) => item.id === data.selected_lesson_id)?.name} Kazanımları</h2><p>Bir alanı ve kazanımı açarak öğrenci yanıtlarını inceleyin.</p></div><span>En az {data.policy.min_attempts} yanıt · %80 iyi · %60 pekiştirilmeli</span></div>
            {data.areas.map((area) => {
              const distribution = area.outcomes.reduce((sum, outcome) => { STATUS_KEYS.forEach((key) => { sum[key] += outcome.distribution[key] || 0; }); return sum; }, { good: 0, practice: 0, support: 0, insufficient: 0 });
              const expanded = areaId === area.id;
              return <div className="mo-area" key={area.id}><button type="button" className="mo-area__button" aria-expanded={expanded} onClick={() => { setAreaId(expanded ? null : area.id); setOutcomeId(null); }}><span className="mo-area__chevron">⌄</span><span className="mo-area__name"><strong>{area.name}</strong><small>{area.outcomes.length} kazanım</small></span><Distribution counts={distribution} /><ChevronRightRounded className="mo-area__arrow" /></button>{expanded && <div className="mo-outcomes">{area.outcomes.map((outcome) => <div className="mo-outcome" key={outcome.id}><button type="button" className="mo-outcome__button" aria-expanded={outcomeId === outcome.id} onClick={() => setOutcomeId(outcomeId === outcome.id ? null : outcome.id)}><OutcomeLabel outcome={outcome} /><Distribution counts={outcome.distribution} /></button>{selectedOutcome?.id === outcome.id && <div className="mo-evidence"><p>Öğrenci durumu · son {data.days} gün</p>{outcome.students.map((student) => <div className="mo-evidence__row" key={student.student_id}><StudentAvatar student={{ id: student.student_id, name: student.name }} size={38} /><strong>{student.name}</strong><OutcomeEvidence evidence={student} /></div>)}</div>}</div>)}</div>}</div>;
            })}
          </section>}
          {tab === 'students' && <section className="mo-content" aria-label="Öğrenci kazanım durumları"><div className="mo-section-heading"><div><h2>Öğrenciler</h2><p>Öğrenci seçerek her kazanımdaki doğru ve toplam yanıtları görün.</p></div></div><div className="mo-student-layout"><div className="mo-student-list">{data.students.map((student) => <button type="button" key={student.id} className={studentId === student.id ? 'is-active' : ''} onClick={() => setStudentId(student.id)}><StudentAvatar student={student} /><span className="mo-student-list__copy"><strong>{student.name}</strong><small>#{student.student_no} · {student.attempt_count} soru yanıtladı</small></span><ChevronRightRounded /></button>)}</div><div className="mo-student-detail">{selectedStudent ? <><div className="mo-student-detail__header"><StudentAvatar student={selectedStudent} size={56} /><div><h3>{selectedStudent.name}</h3><p>Son {data.days} gün · {selectedStudent.correct_count}/{selectedStudent.attempt_count} doğru</p></div></div>{data.areas.map((area) => <div className="mo-student-area" key={area.id}><h4>{area.name}</h4>{area.outcomes.map((outcome) => { const evidence = selectedStudent.outcomes.find((item) => item.outcome_id === outcome.id); return <div className="mo-student-outcome" key={outcome.id}><OutcomeLabel outcome={outcome} />{evidence && <OutcomeEvidence evidence={evidence} />}</div>; })}</div>)}</> : <p>Bir öğrenci seçin.</p>}</div></div></section>}
          <p className="mo-method">Durumlar yalnızca tamamlanmış Mini Görev turlarındaki, ilgili sınıf düzeyine bağlı kazanımlı sorulardan hesaplanır. Matematik Yolculuğu ve tekrar turlarının yanıtları birlikte değerlendirilir; normal dört işlem sonuçları bu rapora dahil değildir. İngilizce Yolculuğu ve tekrar turları ayrı izlenir; Vocabulary ve Word Builder yanıtları İngilizce Yolculuğu kazanımlarına dahil edilmez. Beşten az yanıt “Yeterli veri yok” olarak gösterilir.</p>
        </>}
      </main>
    </Box>
  );
}
